// The files on a session (TX-US-0028): what is attached, what is on its way, and the way to add.
//
// The input names no type, which is what makes a phone offer everything it has - the camera, the
// photo library, the file picker. A file is downloaded, never opened in the page.

import { useRef } from 'react';

import { getHubAttachmentDownload } from '../api.js';
import { STATES, formatBytes } from '../deliveries/attachments/uploads.js';
import Icon from '../ui/Icon.jsx';

const STATE_LABEL = {
  [STATES.queued]: 'Waiting',
  [STATES.uploading]: 'Uploading…',
  [STATES.done]: 'Uploaded',
  [STATES.failed]: 'Failed',
};

// A signed URL is cross-origin, so the browser navigates to it; a new tab keeps the hub open.
function open(url) {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener';
  link.click();
}

export function downloadHubFile(sessionId, attachmentId, onError) {
  return getHubAttachmentDownload(sessionId, attachmentId)
    .then((found) => open(found.download_url))
    .catch((err) => onError(err.message));
}

export default function HubFiles({ sessionId, files, uploads, room, onAdd, onRemove, onError }) {
  const input = useRef(null);

  const choose = (event) => {
    onAdd(event.target.files);
    // Reset so the same file can be chosen again after it was removed.
    event.target.value = '';
  };

  return (
    <section className="hub-files" aria-label="Files">
      {(files.length > 0 || uploads.items.length > 0) && (
        <ul className="hub-file-list">
          {files.map((file) => (
            <li key={file.attachment_id}>
              <button type="button" className="hub-file-name"
                      onClick={() => downloadHubFile(sessionId, file.attachment_id, onError)}
                      aria-label={`Download ${file.filename}`}>
                <strong>{file.filename}</strong>
                <small>{formatBytes(file.size_bytes ?? 0)}</small>
              </button>
              <button type="button" className="icon-button quiet"
                      onClick={() => onRemove(file.attachment_id)}
                      aria-label={`Remove ${file.filename}`} title="Remove">
                <Icon name="trash" />
              </button>
            </li>
          ))}
          {uploads.items.map((item) => (
            <li key={item.key} className={`pending ${item.state}`}>
              <span className="hub-file-name">
                <strong>{item.file.name}</strong>
                <small>
                  {formatBytes(item.file.size)} · {STATE_LABEL[item.state]}
                  {item.error && <span className="error"> — {item.error}</span>}
                </small>
              </span>
              {item.state !== STATES.uploading && (
                <button type="button" className="icon-button quiet"
                        onClick={() => uploads.drop(item.key)}
                        aria-label={`Remove ${item.file.name}`} title="Remove">
                  <Icon name="close" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <input ref={input} type="file" multiple hidden onChange={choose} />
      <button type="button" className="hub-attach" disabled={room === 0}
              onClick={() => input.current.click()}>
        <Icon name="paperclip" />
        <span>{room === 0 ? 'No more files on this update' : 'Add a photo or file'}</span>
      </button>
    </section>
  );
}
